import { useState, useContext } from "react"
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

import useIntlHelper from 'hooks/useIntlHelper'
import { toastDefaultSettings } from 'invariants'
import { useManageExternalLinkMutation, useGetDashboardInfoMutation, useCreateDashboardMutation } from 'services/dashboard'
import { useCreateMenuMutation } from 'services/parentMenu'
import { useCreateMenuMutation as createSubMenuContext } from 'services/submenus';

import { IFormikValues } from 'interfaces'
import { Dashboard } from 'types/dashboard'
import { selectDashboardData } from '@redux/reducers/dashboardInfo'

import Context from '../Context'
import ManageExternalLink from "./ManageExternalLink"
import ClientContext from '../Context';

import { barChartBlack, barChart} from 'assets/icons';

interface DashboardResponse {
  data: {
    id: number;
  };
  error?: {
    data: {
      errors: Record<string, any>;
    };
  };
}


const useManageExternalLink = () => {
  const { isEditMode, setEditMode, setNewParentName, setNewSubItemMenuName, setNewIconArry, setNewUrl, setStep } = useContext(ClientContext);
  const { data: dashboardInfo = {}, isLoading: isLoadingInfo }: any = useSelector(selectDashboardData);
  const [fetchDashboardInfo]: any = useGetDashboardInfoMutation()

  const [manageExternalLink, { isLoading: isLoadingDialog }] = useManageExternalLinkMutation()
  const [createNewDashboard] = useCreateDashboardMutation()
  const [createNewParentMenu] = useCreateMenuMutation()
  const [createNewSubMenu] = createSubMenuContext()
  

  const { companyId, userData, getDashboards, addItemToParentMenu, dashboardId, newParentName, newSubItemMenuName, newIconArry } = useContext(Context)
  const [isOpen, setOpen] = useState(false)
  const [isLinkValid, setLinkValid] = useState(true)
  const [errors, setErrors] = useState<any>({})

  const [isEditModeLocal, setEditModeLocal] = useState(false)
  const { getIntlMessage } = useIntlHelper()

  const handleSetOpen = (open: boolean, editMode: boolean) => {
    setEditModeLocal(editMode)
    setOpen(open)
  }

  const handleCreate = (values: IFormikValues) => {
    const isLinkValid = values.link.includes(process.env.REACT_APP_THIRD_PARTY_URL as string)

    if (isLinkValid) {
      manageExternalLink({
        data: {
          name: values.dashboardName || (dashboardInfo as Dashboard)?.name,
          is_hidden: false,
          link: values.link
        },
        companyId,
        dashboardId
      }).then((resp: any) => {
        if (!resp.hasOwnProperty('error')) {
          fetchDashboardInfo({
            companyId, dashboardId
          })
          setErrors({})
          getDashboards()
          toast.success(getIntlMessage(`successfully_message_after_${isEditModeLocal ? 'edit' : 'add_link'}_dashboard`), toastDefaultSettings)
          handleClose()
        } else {
          setErrors(resp?.error?.data?.errors || {})
        }
      })
    } else {
      setLinkValid(false)
    }
  }

  const createDashboardEntry = async (name: string, companyId: number): Promise<number> => {
    try {
      const resp: any = await createNewDashboard({
        data: {
          name: name,
          is_hidden: false,
        },
        companyId,
      });
  
      if (!resp || resp.hasOwnProperty('error')) {
        setErrors('Failed to create dashboard.');
        throw new Error('Failed to create dashboard.');
      }
  
      if (!resp.data || !resp.data.id) {
        throw new Error('Response data is missing the dashboard ID.');
      }
  
      return resp.data.id; // Explicitly return the dashboard ID
    } catch (error) {
      console.error("Error in createDashboardEntry:", error);
      throw error; // Re-throw the error to be caught in the caller
    }
  };
  

  const linkDashboard = async (name: string, companyId: number, dashboardId: number, link: string): Promise<void> => {
    try {
      const linkResp: any = await manageExternalLink({
        data: {
          name: name,
          is_hidden: false,
          link: link,
        },
        companyId,
        dashboardId,
      });
  
      if (!linkResp || linkResp.hasOwnProperty('error')) {
        setErrors(linkResp?.error?.data?.errors || {});
        throw new Error('Failed to manage external link.');
      }
    } catch (error) {
      console.error("Error in linkDashboard:", error);
      throw error;
    }
  };

  const createDashboard = (name: string, companyId: number, link: string): Promise<number | Error> => {
    return createDashboardEntry(name, companyId)
      .then((dashboard_Id) => {
        return linkDashboard(name, companyId, dashboard_Id, link).then(() => {
          return dashboard_Id; // Return dashboard ID after linking
        });
      })
      .catch((error) => {
        console.error("Error in createDashboard:", error);
        return Promise.reject(error);
      });
  };
  

  const createParentMenu = async (data: any): Promise<number | Error> => {
    try {
      const resp: any = await createNewParentMenu({data});
  
      if (resp.hasOwnProperty('error')) {
        setErrors('Failed to create parentMenu.');
        throw new Error('Failed to create parentMenu.');
      }
  
      // const dashboard_Id = resp;
      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }

  const createSubMenu = async (data: any): Promise<number | Error> => {
    try {
      const resp: any = await createNewSubMenu({data});
  
      if (resp.hasOwnProperty('error')) {
        setErrors('Failed to create subMenu.');
        throw new Error('Failed to create subMenu.');
      }

      return resp;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  }
  

  const updatedUrl = async (v: IFormikValues) => {

    let parentMenu_Id = 0;
    let dashboard_ID: Number | Error = 0;
    let main_menu_order = 0;
    let sub_menu_order = 0;

    const parentMenu = {
      name: newParentName,
      order: main_menu_order,
      company_id: companyId,
      creator_id: userData.id,
      editor_id: userData.id,
      is_hidden: false,
      icons: newIconArry,
    }

    try {
      dashboard_ID= await createDashboard(newSubItemMenuName, Number(companyId),  v.link);

      const parentMenuResponse: any = await createParentMenu(parentMenu);
      parentMenu_Id = parentMenuResponse.data.id;
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    }
    
    const submenus = {
      mainMenu: parentMenu_Id,
      name: newSubItemMenuName,
      order: sub_menu_order,
      dashboardID: dashboard_ID,
      creator_id: userData.id,
      route: v.link
    }

    try {
      const subMenuResponse: Number | Error = await createSubMenu(submenus);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    }
    
    // addItemToParentMenu(menuItems)
    setEditMode(false);
    setStep(0);
    setNewParentName('');
    setNewSubItemMenuName('');
    setNewIconArry('');
    setNewUrl('');
    setEditModeLocal(false)
    handleClose()
  }

  const updatedUrlDashboarMenu = async (v: IFormikValues) => {

    let dashboard_ID: Number | Error = 0;

    try {
      dashboard_ID= await createDashboard(newSubItemMenuName, Number(companyId),  v.link);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    }
    
    setEditMode(false);
    setStep(0);
    setNewParentName('');
    setNewSubItemMenuName('');
    setNewIconArry('');
    setNewUrl('');
    setEditModeLocal(false)
    handleClose()
  }

  const handleClose = () => {
    setErrors({})
    setOpen(false)
  }

  return {
    el: (
      <ManageExternalLink
        open={isOpen}
        isEditMode={isEditModeLocal}
        onClose={handleClose}
        onApply={updatedUrlDashboarMenu}
        errors={errors}
        isLinkValid={isLinkValid}
        isLoading={isLoadingInfo || isLoadingDialog}
        dashboardInfo={dashboardInfo as Dashboard}
      />
    ),
    setOpen: handleSetOpen
  }
}

export default useManageExternalLink